import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getApiUrl } from '../../config/api';
import { RootState } from '../index';

export interface UserEmailCredentials {
  id?: number;
  smtp_tls: boolean;
  smtp_port: number;
  smtp_host: string;
  smtp_user: string;
  smtp_password: string;
  from_email_address: string;
  emails_from_name: string;
  closing_salutation?: string;
  closing_name?: string;
  is_active?: boolean;
  user_id?: number;
  created_at?: string;
  updated_at?: string;
}

export interface CreateEmailCredentialsRequest {
  smtp_tls: boolean;
  smtp_port: number;
  smtp_host: string;
  smtp_user: string;
  smtp_password: string;
  from_email_address: string;
  emails_from_name: string;
  closing_salutation?: string;
  closing_name?: string;
}

export interface UpdateEmailCredentialsRequest {
  smtp_tls: boolean;
  smtp_port: number;
  smtp_host: string;
  smtp_user: string;
  smtp_password: string;
  from_email_address: string;
  emails_from_name: string;
  closing_salutation?: string;
  closing_name?: string;
}

export const userEmailCredentialsApi = createApi({
  reducerPath: 'userEmailCredentialsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: getApiUrl('/api/v1/users/email-credentials'),
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['UserEmailCredentials'],
  endpoints: (builder) => ({
    // Get email credentials
    getEmailCredentials: builder.query<UserEmailCredentials | null, void>({
      query: () => '/',
      providesTags: ['UserEmailCredentials'],
    }),
    
    // Create email credentials
    createEmailCredentials: builder.mutation<UserEmailCredentials, CreateEmailCredentialsRequest>({
      query: (credentialsData) => ({
        url: '/',
        method: 'POST',
        body: credentialsData,
      }),
      invalidatesTags: ['UserEmailCredentials'],
    }),
    
    // Update email credentials
    updateEmailCredentials: builder.mutation<UserEmailCredentials, UpdateEmailCredentialsRequest>({
      query: (credentialsData) => ({
        url: '/',
        method: 'PUT',
        body: credentialsData,
      }),
      invalidatesTags: ['UserEmailCredentials'],
    }),
    
    // Delete email credentials
    deleteEmailCredentials: builder.mutation<{ message: string }, void>({
      query: () => ({
        url: '/',
        method: 'DELETE',
      }),
      invalidatesTags: ['UserEmailCredentials'],
    }),
  }),
});

export const {
  useGetEmailCredentialsQuery,
  useCreateEmailCredentialsMutation,
  useUpdateEmailCredentialsMutation,
  useDeleteEmailCredentialsMutation,
} = userEmailCredentialsApi;
